import type { Light } from '@anov/3d-core'
import { DirectionalLight, MathUtils, Object3D, Vector3, lib } from '@anov/3d-core'
import { getSunPosition } from '../utils/math'

const { Sky } = lib

type SkySystemOption = {
  scalarCoefficient?: number
  turbidity?: number
  rayleigh?: number
  mieCoefficient?: number
  mieDirectionalG?: number
  elevation?: number
  azimuth?: number
  exposure?: number
}

const effectController: SkySystemOption = {
  turbidity: 10,
  rayleigh: 3,
  mieCoefficient: 0.005,
  mieDirectionalG: 0.7,
  elevation: 2,
  azimuth: 180,
  exposure: 0.1,
}

/**
 * 天空——时间系统
 */
export class SkySystem extends Object3D {
  private _size = 450000
  private _sky: lib.Sky
  private _effectController: SkySystemOption = effectController
  private _latitude = 40
  private _longitude = 116
  private _sunHeight = 10000
  private _sun: Light

  public get size() {
    return this.size
  }

  public set size(size: number) {
    this._size = size
    this._sky.scale.setScalar(this._size)
  }

  public get sunHeight() {
    return this._sunHeight
  }

  public set sunHeight(height: number) {
    this._sunHeight = height
  }

  constructor(options?: SkySystemOption) {
    super()

    this._effectController = {
      ...effectController,
      ...options ?? {},
    }
    this.createSky()
    this.createSun()
    this.combination()
  }

  /**
   * create sky
   * @returns
   */
  private createSky() {
    this._sky = new Sky()
    this._sky.scale.setScalar(this._size)

    const position = new Vector3()
    const uniforms = (this._sky.material as any).uniforms
    const phi = MathUtils.degToRad(90 - this._effectController.elevation)
    const theta = MathUtils.degToRad(this._effectController.azimuth)

    uniforms.turbidity.value = this._effectController.turbidity
    uniforms.rayleigh.value = this._effectController.rayleigh
    uniforms.mieCoefficient.value = this._effectController.mieCoefficient
    uniforms.mieDirectionalG.value = this._effectController.mieDirectionalG

    position.setFromSphericalCoords(1, phi, theta)
    uniforms.sunPosition.value.copy(position)

    return this._sky
  }

  /**
   * create sun
   * @returns
   */
  private createSun() {
    const sunLight = new DirectionalLight('white', 1)
    sunLight.name = 'SYS_SUN'
    sunLight.castShadow = true
    this._sun = sunLight

    // 添加帮助
    // const helper = new DirectionalLightHelper(sunLight, 200)
    // this.add(helper)

    return sunLight
  }

  private combination() {
    this.setTime(new Date())
    this.add(this._sky)
    this.add(this._sun)
  }

  /**
   * 设置天空中的太阳位置
   * @param position
   */
  setSunPositin(position: Vector3) {
    const uniforms = (this._sky.material as any).uniforms
    uniforms.sunPosition.value.copy(position)
  }

  /**
   * 设置时间
   * @param currentDate
   */
  setTime(currentDate?: Date) {
    // 根据时间、经纬度获取太阳的高度角(altitude)、方位角(azimuth)
    const { altitude, azimuth } = getSunPosition(currentDate, this._latitude, this._longitude)
    const uniforms = (this._sky.material as any).uniforms

    // 转换为 Three.js setFromSphericalCoords 参数
    const phi = Math.PI / 2 - altitude // SunCalc的高度角转换为Three.js的phi
    const theta = azimuth + Math.PI // SunCalc的方位角转换为Three.js的theta
    const sunPosition = new Vector3().setFromSphericalCoords(1, phi, theta)
    uniforms.sunPosition.value.copy(sunPosition)

    // 缩放向量使光源足够远但不超出场景有效范围
    const scaledPosition = sunPosition.clone().multiplyScalar(this._sunHeight)
    this._sun.position.copy(scaledPosition)

    // uniforms.sunPosition.value.copy(new Vector3().setFromSphericalCoords(this._sunHeight, MathUtils.degToRad(90 - MathUtils.radToDeg(altitude)), azimuth))
    // const x = this._sunHeight * (Math.cos(altitude)) * (Math.cos(azimuth))
    // const z = this._sunHeight * (Math.cos(altitude)) * (Math.sin(azimuth))
    // const y = this._sunHeight * (Math.sin(altitude))
    // this._sun.position.copy(new Vector3(x, y, z))
  }

  public updateSky(camera) {
    if (this._sky)
      this._sky.position.copy(camera.position)
  }
}